import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const usernameLooksLikeEmail = (value) => /@/.test(value) && /\./.test(value);
const hasWhitespace = (value) => /\s/.test(value);

const LoginForm = ({ orgPin }) => {
    const { login, error: authError, clearError, loading } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [formError, setFormError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Extract error from URL if present (for redirects with error params)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        if (errorParam) {
            const map = {
                invalid: 'Invalid username or password',
                expired: 'Your session has expired. Please login again.',
                inactive: 'Your account is inactive. Please contact support.',
                maintenance: 'System is currently in maintenance mode.',
                unauthorized: 'You are not authorized to access this resource.'
            };
            setFormError(map[errorParam] || 'An error occurred. Please try again.');
        }
    }, []);

    // Clear context error when component unmounts
    useEffect(() => () => clearError(), [clearError]);

    // Keep local error in sync with context error
    useEffect(() => {
        if (authError) setFormError(authError);
    }, [authError]);

    const usernameIssue = useMemo(() => {
        if (!username.trim()) return 'Username is required';
        if (hasWhitespace(username)) return 'Username cannot contain spaces';
        if (!usernameLooksLikeEmail(username) && username.length < 3) return 'Username must be at least 3 characters';
        return '';
    }, [username]);

    const passwordIssue = useMemo(() => {
        if (!password) return 'Password is required';
        if (hasWhitespace(password)) return 'Password cannot contain spaces';
        if (password.length < 8) return 'Password must be at least 8 characters';
        return '';
    }, [password]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!orgPin) {
            setFormError('Organization PIN is required');
            return;
        }
        if (usernameIssue) {
            setFormError(usernameIssue);
            return;
        }
        if (passwordIssue) {
            setFormError(passwordIssue);
            return;
        }
        setFormError('');
        try {
            const result = await login(username.trim(), password, String(orgPin), rememberMe);
            if (!result.success) setFormError(result.error || 'Authentication failed');
        } catch {
            setFormError('An unexpected error occurred. Please try again.');
        }
    };

    return (
        <div className="w-full">
            {formError && (
                <div data-testid="form-error" className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 mb-4">
                    {formError}
                </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
                <input type="hidden" name="orgPin" value={orgPin || ''} />

                <div>
                    <label htmlFor="username" className="text-sm font-medium block mb-1">Username</label>
                    <input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => {
                            setUsername(e.target.value);
                            if (formError) setFormError('');
                        }}
                        className="w-full px-3 py-2 border rounded-md"
                        disabled={loading}
                        autoComplete="username"
                        autoCapitalize="none"
                        required
                        aria-invalid={!!usernameIssue}
                        aria-describedby="username-help"
                    />
                    {usernameIssue && <p id="username-help" className="mt-1 text-xs text-red-600">{usernameIssue}</p>}
                </div>

                <div className="relative">
                    <label htmlFor="password" className="text-sm font-medium block mb-1">Password</label>
                    <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            if (formError) setFormError('');
                        }}
                        className="w-full px-3 py-2 border rounded-md pr-10"
                        disabled={loading}
                        autoComplete="current-password"
                        autoCapitalize="none"
                        required
                        aria-invalid={!!passwordIssue}
                        aria-describedby="password-help"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-8 text-gray-500"
                        aria-pressed={showPassword}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                                <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                            </svg>
                        )}
                    </button>
                    {passwordIssue && <p id="password-help" className="mt-1 text-xs text-red-600">{passwordIssue}</p>}
                </div>

                <div className="flex items-center space-x-2">
                    <input
                        id="remember"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4"
                        disabled={loading}
                    />
                    <label htmlFor="remember" className="text-sm font-normal">Remember me</label>
                </div>

                <button type="submit" className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white py-2 rounded-md" disabled={loading}>
                    {loading ? 'Signing in...' : 'Sign in'}
                </button>

                <div className="text-center">
                    <a href="/View/Forgot_Password.php" className="text-sm text-[#3B82F6] hover:text-[#2563EB]">Forgot Password?</a>
                </div>

                <div className="text-center mt-2">
                    <a href="/login-legacy.php" className="text-sm text-muted-foreground hover:underline">(Use Legacy Login)</a>
                </div>
            </form>
        </div>
    );
};

export default LoginForm;

